// src/components/Category/CategoryTable.jsx
// Component to display category data in a table format with modern design

import React from 'react';
import RoleBasedComponent from '../common/RoleBasedComponent';

/**
 * CategoryTable Component
 * Displays a list of categories in a table format with edit and delete actions
 * 
 * @param {Array} categories - Array of category objects to display
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const CategoryTable = ({ categories, onEdit, onDelete, isLoading }) => {

  /**
   * Handle edit button click
   * @param {Object} category - The category item to edit
   */
  const handleEdit = (category) => {
    if (onEdit) {
      onEdit(category);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} category - The category item to delete
   */
  const handleDelete = (category) => {
    if (onDelete) {
      onDelete(category);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th style={{ width: '100px' }}>ID</th>
              <th>Category Name</th>
              <th style={{ width: '150px' }}>Status</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="4" className="inventory-loading">
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '12px' 
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E5E7EB',
                    borderTop: '4px solid #004080',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Loading category data...</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show empty state
  if (!categories || categories.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th style={{ width: '100px' }}>ID</th>
              <th>Category Name</th>
              <th style={{ width: '150px' }}>Status</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="4" className="inventory-empty-state">
                <div className="empty-state-container">
                  <svg 
                    width="48" 
                    height="48" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#D1D5DB" 
                    strokeWidth="2"
                    style={{ marginBottom: '12px' }}
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    <line x1="9" y1="7" x2="15" y2="7"></line>
                  </svg>
                  <div className="empty-state-title">
                    No categories found
                  </div>
                  <div className="empty-state-message">
                    Try adjusting your search or add a new category
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th >ID</th>
            <th >Category Name</th>
            <th >Status</th>
            <th >Actions</th>
          </tr>
        </thead>

        <tbody>
          {categories.map((category) => (
            <tr key={category.id || category.categoryId}>
              
              {/* Category ID */}
              <td style={{ fontWeight: '500', color: '#6B7280' }}>
                CAT-{String(category.categoryId || category.id).padStart(4, '0')}
              </td>
              
              {/* Category Name */}
              <td style={{ fontWeight: '600' }}>
                {category.name}
              </td>
              
              {/* Status Badge */}
              <td>
                <span className={`category-status-badge ${
                  category.isActive ? 'category-status-active' : 'category-status-inactive'
                }`}>
                  {category.isActive ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      Active
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Inactive
                    </>
                  )}
                </span>
              </td>
              
              {/* Actions */}
              <td>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="inventory-btn-action inventory-btn-edit"
                    onClick={() => handleEdit(category)}
                    title="Edit this category"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit
                  </button>
                  
                  <RoleBasedComponent allowedRoles={['superadmin']}>
                    <button
                      className="inventory-btn-action inventory-btn-delete"
                      onClick={() => handleDelete(category)}
                      title="Delete this category"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      Delete
                    </button>
                  </RoleBasedComponent>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CategoryTable;